import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useVault } from '../contexts/VaultContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Pill, Plus, Trash2, ShieldCheck, Volume2, Search, Clock, Calendar, Utensils, Edit2, Camera, Loader2, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import EncryptionBadge from '../components/EncryptionBadge';
import { 
  searchMedicines, 
  FREQUENCY_OPTIONS, 
  TIMING_OPTIONS, 
  DURATION_OPTIONS 
} from '../data/medicinesDatabase';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrescriptionsPage = () => {
  const { t, language, speak } = useLanguage();
  const { api, isUnlocked } = useVault();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const fileInputRef = useRef(null);
  
  // Autocomplete state
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [skipNextSearch, setSkipNextSearch] = useState(false);
  const searchRef = useRef(null);
  
  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    timing: '',
    duration: '',
    doctor_name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    notes: ''
  });

  useEffect(() => {
    if (isUnlocked) {
      fetchEntries();
    }
  }, [isUnlocked]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search medicines as user types
  useEffect(() => {
    if (skipNextSearch) {
      setSkipNextSearch(false);
      return;
    }
    if (searchQuery.length >= 2 && !selectedMedicine) {
      const results = searchMedicines(searchQuery);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, skipNextSearch, selectedMedicine]);

  const fetchEntries = async () => {
    try {
      // Use encrypted API
      const data = await api.getPrescriptions();
      setEntries(data || []);
    } catch (error) {
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMedicine = (medicine) => {
    setSkipNextSearch(true);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedMedicine(medicine);
    setSearchQuery(medicine.name);
    setFormData({ ...formData, medication_name: medicine.name });
  };

  const handleSelectDosage = (dosage) => {
    setFormData({ 
      ...formData, 
      medication_name: `${selectedMedicine?.name || searchQuery} ${dosage}`,
      dosage: dosage 
    });
  };

  const calculateEndDate = (duration) => {
    if (!duration || duration === 'ongoing') return '';
    
    const days = {
      '3_days': 3,
      '5_days': 5,
      '7_days': 7,
      '10_days': 10,
      '14_days': 14,
      '1_month': 30,
      '2_months': 60,
      '3_months': 90,
      '6_months': 180,
    };
    
    if (days[duration]) {
      const end = new Date();
      end.setDate(end.getDate() + days[duration]);
      return end.toISOString().split('T')[0];
    }
    return '';
  };

  const handleDurationChange = (duration) => {
    const endDate = calculateEndDate(duration);
    setFormData({ ...formData, duration, end_date: endDate });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build full frequency string
    const freqOption = FREQUENCY_OPTIONS.find(f => f.value === formData.frequency);
    const timingOption = TIMING_OPTIONS.find(t => t.value === formData.timing);
    const fullFrequency = [
      freqOption ? (language === 'hi' ? freqOption.hi : freqOption.en) : formData.frequency,
      timingOption ? (language === 'hi' ? timingOption.hi : timingOption.en) : ''
    ].filter(Boolean).join(', ');
    
    try {
      const payload = {
        ...formData,
        frequency: fullFrequency
      };
      
      if (editingEntry) {
        // Use encrypted API
        await api.updatePrescription(editingEntry.id, payload);
        toast.success(language === 'hi' ? 'अपडेट हो गया!' : 'Prescription updated!');
      } else {
        // Use encrypted API
        await api.createPrescription(payload);
        toast.success(language === 'hi' ? 'दवाई जोड़ी गई!' : 'Prescription added!');
      }
      setDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(editingEntry ? 'Failed to update prescription' : 'Failed to add prescription');
    }
  };

  const resetForm = () => {
    setFormData({
      medication_name: '',
      dosage: '',
      frequency: '',
      timing: '',
      duration: '',
      doctor_name: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      notes: ''
    });
    setSearchQuery('');
    setSelectedMedicine(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setSkipNextSearch(false);
    setEditingEntry(null);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      medication_name: entry.medication_name || '',
      dosage: entry.dosage || '',
      frequency: entry.frequency || '',
      timing: entry.timing || '',
      duration: entry.duration || '',
      doctor_name: entry.doctor_name || '',
      start_date: entry.start_date || new Date().toISOString().split('T')[0],
      end_date: entry.end_date || '',
      notes: entry.notes || ''
    });
    setSearchQuery(entry.medication_name || '');
    setDialogOpen(true);
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleDelete = async (id) => {
    setDeleteConfirmId(id);
  };
  
  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await api.deletePrescription(deleteConfirmId);
      toast.success(language === 'hi' ? 'हटा दिया गया' : 'Deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  // Scan prescription with camera/image
  const handleScanPrescription = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'hi' ? 'कृपया एक फोटो चुनें' : 'Please select an image');
      return;
    }

    setScanning(true);
    setScanDialogOpen(true);

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Image = reader.result.split(',')[1];
        
        try {
          // Send to backend for OCR
          const response = await axios.post(`${API_URL}/prescriptions/scan`, {
            image_base64: base64Image,
            language: language
          });

          if (response.data.prescriptions && response.data.prescriptions.length > 0) {
            setScannedData(response.data.prescriptions);
            toast.success(language === 'hi' 
              ? `${response.data.prescriptions.length} दवाइयां मिलीं!` 
              : `Found ${response.data.prescriptions.length} medicines!`);
          } else {
            toast.info(language === 'hi' 
              ? 'कोई दवाई नहीं मिली। कृपया साफ फोटो लें।' 
              : 'No medicines found. Please try a clearer photo.');
            setScannedData(null);
          }
        } catch (error) {
          console.error('OCR error:', error);
          toast.error(language === 'hi' 
            ? 'स्कैन में गड़बड़ी। पुनः प्रयास करें।' 
            : 'Scan failed. Please try again.');
          setScannedData(null);
        }
      };
      reader.readAsDataURL(file);
    } finally {
      setScanning(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Add scanned prescription
  const addScannedPrescription = async (prescription) => {
    try {
      await axios.post(`${API_URL}/health/prescriptions`, {
        medication_name: prescription.medication_name,
        dosage: prescription.dosage || '',
        frequency: prescription.frequency || '',
        timing: prescription.timing || '',
        duration: prescription.duration || '',
        doctor_name: prescription.doctor_name || '',
        start_date: new Date().toISOString().split('T')[0],
        notes: language === 'hi' ? 'स्कैन से जोड़ा गया' : 'Added from scan'
      });
      toast.success(language === 'hi' 
        ? `${prescription.medication_name} जोड़ा गया!` 
        : `${prescription.medication_name} added!`);
      fetchEntries();
    } catch (error) {
      toast.error('Failed to add prescription');
    }
  };

  const isActive = (endDate) => {
    if (!endDate) return true;
    return new Date(endDate) >= new Date();
  };

  const tt = (en, hi) => language === 'hi' ? hi : en;

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-blue-100">
              <Pill className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('prescriptions')}</h1>
              <div className="flex items-center gap-2">
                <p className="text-slate-500">{entries.length} {tt('medications', 'दवाइयां')}</p>
                <EncryptionBadge variant="compact" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Scan Button */}
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              className="border-[#0D2340] text-[#0D2340]"
              data-testid="scan-prescription-btn"
            >
              <Camera className="h-4 w-4 mr-2" />
              {language === 'hi' ? 'स्कैन' : 'Scan'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              capture="environment"
              onChange={handleScanPrescription}
              className="hidden"
            />
            
            {/* Add Button */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0D2340]" data-testid="add-prescription-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addPrescription')}
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingEntry 
                    ? (language === 'hi' ? 'दवाई संपादित करें' : 'Edit Prescription')
                    : t('addPrescription')
                  }
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Medicine Search with Autocomplete */}
                <div ref={searchRef} className="relative">
                  <Label className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    {tt('Medicine Name', 'दवाई का नाम')}
                  </Label>
                  <Input
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedMedicine(null); // Reset selection when user types
                      setFormData({ ...formData, medication_name: e.target.value, dosage: '' });
                    }}
                    placeholder={tt('Type medicine name...', 'दवाई का नाम टाइप करें...')}
                    className="h-12 text-lg"
                    required
                    data-testid="medication-search-input"
                  />
                  
                  {/* Autocomplete Suggestions */}
                  {showSuggestions && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {suggestions.map((medicine, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectMedicine(medicine)}
                          onTouchEnd={() => handleSelectMedicine(medicine)}
                          className="p-3 hover:bg-[#0D2340]/5 cursor-pointer border-b last:border-b-0 active:bg-[#0D2340]/10"
                          data-testid={`medicine-suggestion-${index}`}
                        >
                          <div className="font-medium">{medicine.name}</div>
                          <div className="text-xs text-slate-500">{medicine.category}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dosage Selection - Show when medicine is selected */}
                {selectedMedicine && selectedMedicine.dosages && (
                  <div>
                    <Label>{tt('Select Dosage', 'खुराक चुनें')}</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedMedicine.dosages.map((dosage, index) => (
                        <Button
                          key={index}
                          type="button"
                          variant={formData.dosage === dosage ? 'default' : 'outline'}
                          className={formData.dosage === dosage ? 'bg-[#0D2340]' : ''}
                          onClick={() => handleSelectDosage(dosage)}
                          data-testid={`dosage-option-${index}`}
                        >
                          {dosage}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual Dosage if not from suggestions */}
                {!selectedMedicine && searchQuery && (
                  <div>
                    <Label>{tt('Dosage', 'खुराक')}</Label>
                    <Input
                      value={formData.dosage}
                      onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                      placeholder={tt('e.g., 500mg or 1 tablet', 'जैसे: 500mg या 1 गोली')}
                      data-testid="manual-dosage-input"
                    />
                  </div>
                )}

                {/* Frequency Selection */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {tt('How Often?', 'कितनी बार?')}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {FREQUENCY_OPTIONS.slice(0, 6).map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant={formData.frequency === option.value ? 'default' : 'outline'}
                        className={`text-sm ${formData.frequency === option.value ? 'bg-[#0D2340]' : ''}`}
                        onClick={() => setFormData({ ...formData, frequency: option.value })}
                      >
                        {language === 'hi' ? option.hi : option.en}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Timing Selection */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Utensils className="h-4 w-4" />
                    {tt('When to Take?', 'कब लें?')}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {TIMING_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant={formData.timing === option.value ? 'default' : 'outline'}
                        className={`text-sm ${formData.timing === option.value ? 'bg-[#2E7D32]' : ''}`}
                        onClick={() => setFormData({ ...formData, timing: option.value })}
                      >
                        {language === 'hi' ? option.hi : option.en}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Duration Selection */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {tt('For How Long?', 'कितने दिन?')}
                  </Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {DURATION_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant={formData.duration === option.value ? 'default' : 'outline'}
                        className={`text-xs ${formData.duration === option.value ? 'bg-[#FF6F00]' : ''}`}
                        onClick={() => handleDurationChange(option.value)}
                      >
                        {language === 'hi' ? option.hi : option.en}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Doctor Name */}
                <div>
                  <Label>{tt('Prescribed by Doctor', 'डॉक्टर का नाम')}</Label>
                  <Input
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                    placeholder={tt('Dr. Name', 'डॉ. का नाम')}
                    required
                    data-testid="prescription-doctor-input"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label>{tt('Special Instructions', 'विशेष निर्देश')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={tt('Any special instructions...', 'कोई विशेष निर्देश...')}
                    rows={2}
                    data-testid="prescription-notes-input"
                  />
                </div>

                {/* Summary Preview */}
                {formData.medication_name && formData.frequency && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">
                      {tt('Summary:', 'सारांश:')}
                    </p>
                    <p className="text-blue-700">
                      {formData.medication_name} {formData.dosage && `(${formData.dosage})`}
                      <br />
                      {FREQUENCY_OPTIONS.find(f => f.value === formData.frequency)?.[language === 'hi' ? 'hi' : 'en']}
                      {formData.timing && `, ${TIMING_OPTIONS.find(t => t.value === formData.timing)?.[language === 'hi' ? 'hi' : 'en']}`}
                      {formData.duration && formData.duration !== 'ongoing' && (
                        <><br />{tt('For', 'के लिए')} {DURATION_OPTIONS.find(d => d.value === formData.duration)?.[language === 'hi' ? 'hi' : 'en']}</>
                      )}
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="save-prescription-btn">
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <Pill className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('noData')}</p>
              <p className="text-slate-400 text-sm mt-2">
                {tt('Add your first prescription', 'अपनी पहली दवाई जोड़ें')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className="card-interactive" data-testid={`prescription-card-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg font-['Manrope']">{entry.medication_name}</h3>
                        <Badge variant={isActive(entry.end_date) ? 'default' : 'secondary'} className={isActive(entry.end_date) ? 'bg-[#2E7D32]' : ''}>
                          {isActive(entry.end_date) ? tt('Active', 'चालू') : tt('Completed', 'पूर्ण')}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-2">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">{tt('Dosage', 'खुराक')}</span>
                          <p className="font-medium">{entry.dosage}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">{tt('Frequency', 'कितनी बार')}</span>
                          <p className="font-medium">{entry.frequency}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">{tt('Doctor', 'डॉक्टर')}</span>
                          <p className="font-medium">{entry.doctor_name}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">{tt('Started', 'शुरू')}</span>
                          <p className="font-medium">{entry.start_date}</p>
                        </div>
                      </div>
                      {entry.notes && <p className="text-slate-600 text-sm">{entry.notes}</p>}
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="blockchain-badge text-xs">
                          <ShieldCheck className="h-3 w-3" />
                          {entry.blockchain_hash?.slice(0, 12)}...
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(entry)}
                        className="text-blue-500 hover:text-blue-700"
                        data-testid={`edit-prescription-${entry.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => speak(`${entry.medication_name}. ${entry.dosage}, ${entry.frequency}`)}
                        data-testid={`speak-prescription-${entry.id}`}
                      >
                        <Volume2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-prescription-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {language === 'hi' ? 'क्या आप वाकई हटाना चाहते हैं?' : 'Delete this prescription?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-500 text-sm">
            {language === 'hi' ? 'यह क्रिया पूर्ववत नहीं की जा सकती।' : 'This action cannot be undone.'}
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button onClick={confirmDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white">
              {language === 'hi' ? 'हटाएं' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scan Results Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={setScanDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {language === 'hi' ? 'स्कैन परिणाम' : 'Scan Results'}
            </DialogTitle>
          </DialogHeader>
          
          {scanning ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#0D2340]" />
              <p className="mt-4 text-slate-500">
                {language === 'hi' ? 'पर्चा पढ़ रहे हैं...' : 'Reading prescription...'}
              </p>
            </div>
          ) : scannedData && scannedData.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {language === 'hi' 
                  ? 'जो दवाइयां जोड़नी हैं उन पर टैप करें:' 
                  : 'Tap medicines to add:'}
              </p>
              {scannedData.map((prescription, index) => (
                <Card 
                  key={index} 
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => {
                    addScannedPrescription(prescription);
                    setScannedData(prev => prev.filter((_, i) => i !== index));
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#0D2340]">{prescription.medication_name}</p>
                        <p className="text-sm text-slate-500">
                          {prescription.dosage && `${prescription.dosage}`}
                          {prescription.frequency && ` • ${prescription.frequency}`}
                        </p>
                      </div>
                      <Plus className="h-5 w-5 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button 
                variant="outline" 
                onClick={() => setScanDialogOpen(false)}
                className="w-full mt-4"
              >
                {language === 'hi' ? 'बंद करें' : 'Close'}
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Camera className="h-12 w-12 mx-auto text-slate-300" />
              <p className="mt-4 text-slate-500">
                {language === 'hi' 
                  ? 'कोई दवाई नहीं मिली' 
                  : 'No medicines found'}
              </p>
              <Button 
                onClick={() => {
                  setScanDialogOpen(false);
                  fileInputRef.current?.click();
                }}
                className="mt-4"
              >
                {language === 'hi' ? 'फिर से स्कैन करें' : 'Try Again'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PrescriptionsPage;
